import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { KeyRound, ArrowLeft, CheckCircle2 } from 'lucide-react';
import { ResetPasswordSchema, type ResetPasswordInput } from '@cutlyft/zod-schemas';
import { useResetPassword } from '@/hooks/auth/use-reset-password';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';

export default function ResetPasswordPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const resetPasswordMutation = useResetPassword();
  const token = searchParams.get('token') || '';

  const form = useForm<Omit<ResetPasswordInput, 'token'> & { confirmPassword: string }>({
    defaultValues: {
      newPassword: '',
      confirmPassword: '',
    },
  });

  const onSubmit = (data: any) => {
    if (data.newPassword !== data.confirmPassword) {
      form.setError('confirmPassword', { message: 'Passwords do not match' });
      return;
    }

    if (!token) {
      form.setError('root', { message: 'Invalid or missing password reset token.' });
      return;
    }

    // Call the mutation using the actual Zod schema
    resetPasswordMutation.mutate({
      token,
      newPassword: data.newPassword,
    }, {
      onSuccess: () => {
        // Redirect to login after a brief delay
        setTimeout(() => {
          navigate('/login');
        }, 3000);
      },
      onError: (err: any) => {
        const apiError = err.response?.data?.error;
        form.setError('root', {
          message: apiError?.message || 'Failed to reset password. The link may have expired.',
        });
      },
    });
  };

  const isSuccess = resetPasswordMutation.isSuccess;

  return (
    <div className="flex min-h-dvh flex-col items-center justify-center bg-bg-secondary p-6 relative overflow-hidden">
      {/* Decorative premium gradient background glow */}
      <div className="absolute top-[-10%] right-[-10%] w-[500px] h-[500px] rounded-full bg-primary/10 blur-[120px] pointer-events-none" />
      <div className="absolute bottom-[-10%] left-[-10%] w-[500px] h-[500px] rounded-full bg-accent/5 blur-[120px] pointer-events-none" />

      <div className="w-full max-w-md z-10 space-y-6">
        {/* Brand logo display */}
        <div className="text-center">
          <span className="text-3xl font-extrabold tracking-tight text-primary">Cutlyft</span>
        </div>

        <Card className="border border-border/50 shadow-xl bg-bg-elevated/80 backdrop-blur-md">
          {!isSuccess ? (
            <>
              <CardHeader>
                <CardTitle className="text-2xl text-fg-primary flex items-center gap-2">
                  <KeyRound className="h-6 w-6 text-primary" />
                  <span>Choose New Password</span>
                </CardTitle>
                <CardDescription className="text-fg-secondary">
                  Please enter and confirm your new password below.
                </CardDescription>
              </CardHeader>
              <CardContent>
                {!token && (
                  <div className="p-3 mb-4 text-sm rounded bg-error-bg text-error border border-error/20">
                    ⚠️ Error: Reset token is missing. Please request a new password reset email.
                  </div>
                )}
                <Form {...form}>
                  <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
                    {form.formState.errors.root && (
                      <div className="p-3 text-sm rounded bg-error-bg text-error border border-error/20">
                        {form.formState.errors.root.message}
                      </div>
                    )}

                    <FormField
                      control={form.control}
                      name="newPassword"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>New Password</FormLabel>
                          <FormControl>
                            <Input type="password" placeholder="••••••••" {...field} disabled={!token} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="confirmPassword"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Confirm New Password</FormLabel>
                          <FormControl>
                            <Input type="password" placeholder="••••••••" {...field} disabled={!token} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <Button
                      type="submit"
                      className="w-full mt-2 bg-primary hover:bg-primary/95 text-primary-foreground font-semibold h-11"
                      disabled={resetPasswordMutation.isPending || !token}
                    >
                      {resetPasswordMutation.isPending ? 'Resetting Password...' : 'Save New Password'}
                    </Button>
                  </form>
                </Form>
              </CardContent>
            </>
          ) : (
            <>
              <CardHeader className="text-center">
                <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-success-bg text-success mb-3">
                  <CheckCircle2 className="h-8 w-8" />
                </div>
                <CardTitle className="text-2xl text-fg-primary">Password Reset Successful</CardTitle>
                <CardDescription className="text-fg-secondary pt-1">
                  Your password has been successfully updated. Redirecting you to the login screen...
                </CardDescription>
              </CardHeader>
              <CardContent className="text-center text-sm text-fg-muted">
                You can now log in to your workspace using your new password.
              </CardContent>
            </>
          )}

          <CardFooter className="flex justify-center border-t border-border/30 pt-6">
            <Link to="/login" className="flex items-center gap-2 text-sm font-semibold text-primary hover:underline">
              <ArrowLeft className="h-4 w-4" />
              <span>Back to Login</span>
            </Link>
          </CardFooter>
        </Card>
      </div>
    </div>
  );
}
